import React, { useRef, useEffect, useState } from "react";
import { formatCPFRatio, calculateCPFRatio } from "../../CPFCalculator";
import { ReactComponent as DeleteIcon } from "../../../../../assects/svg/client_details/delete.svg";
import { ReactComponent as DuplicateIcon } from "../../../../../assects/svg/client_details/duplicate.svg";
import useCustomScrollbar from "../../../../styles/useCustomScrollbar";
import { Button } from "../../../../Ui_Components/Button";
import { getAllDishesMealPlan } from "../../../../../services/mealPlans";

export interface DishItem {
  id: string;
  name: string;
  quantity: string;
  unit: string;
  oil: number;
  cal: number;
  gl: number;
  carb: number;
  protein: number;
  fat: number;
  is_hidden?: boolean;
}

interface ViewMealOptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional — hidden when undefined (Active / view-only plan) */
  onEdit?: () => void;
  /** Optional — hidden when undefined (Active / view-only plan) */
  onDelete?: () => void;
  /** Optional — hidden when undefined (Active / view-only plan) */
  onDuplicate?: () => void;
  optionName: string;
  mealType: string;
  dishes: DishItem[];
  instructions?: string;
  totals: {
    cal: number;
    carb: number;
    fat: number;
    protein: number;
    gl: number;
  };
  isDeleting?: boolean;
  /** When true the footer only shows a Close button */
  isViewOnly?: boolean;
  /** User id for the dish-measurements lookup — pass "0" when unavailable
   *  (e.g. adhoc mode has no user in the route). */
  userId?: string | number;
}

const ViewMealOptionModal: React.FC<ViewMealOptionModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  onDuplicate,
  optionName,
  mealType,
  dishes,
  instructions,
  totals,
  isDeleting = false,
  isViewOnly = false,
  userId,
}) => {
  useCustomScrollbar();
  const modalRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  // dishName → { unitType → measure_gram } — sourced from getAllDishesMealPlan
  // so we can render "gm - 1gm" style labels matching the edit dropdown.
  const [unitGramLookup, setUnitGramLookup] = useState<
    Record<string, Record<string, string>>
  >({});

  useEffect(() => {
    if (!isOpen) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await getAllDishesMealPlan(userId ?? "0", "");
        if (ctrl.signal.aborted) return;
        if (data?.success === 1 && Array.isArray(data.dishes)) {
          const lookup: Record<string, Record<string, string>> = {};
          data.dishes.forEach((d: any) => {
            const perUnit: Record<string, string> = {};
            (d.measurements ?? []).forEach((m: any) => {
              if (m?.unit_type) perUnit[m.unit_type] = String(m.measure_gram ?? "");
            });
            lookup[d.dish_name] = perUnit;
          });
          setUnitGramLookup(lookup);
        }
      } catch {
        // Non-fatal — fall back to bare unit label.
      }
    })();
    return () => ctrl.abort();
  }, [isOpen, userId]);

  const formatUnitLabel = (dishName: string, unit: string): string => {
    const gram = unitGramLookup[dishName]?.[unit];
    return gram ? `${unit} - ${gram}gm` : unit;
  };

  // Click outside → close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Reset delete confirmation when modal closes
  useEffect(() => {
    if (!isOpen) setShowDeleteConfirmation(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const cpfRatio = formatCPFRatio(
    calculateCPFRatio(totals.carb, totals.protein, totals.fat, totals.cal).ratio,
  );

  const handleDuplicateClick = () => {
    onDuplicate?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-md w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* ── Content ──────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Header */}
          <div className="flex items-center justify-between mb-4 border-b-[0.5px] border-[#E5E5EA] pb-3">
            <div className="flex flex-col items-start">
              <h2 className="text-[18px] font-medium text-[#1F1F1F]">{optionName}</h2>
              <span className="text-[#828282] text-sm">{mealType}</span>
            </div>

            {/* Duplicate icon — hidden when view-only */}
            {!isViewOnly && onDuplicate && (
              <button
                onClick={handleDuplicateClick}
                disabled={isDeleting}
                className="flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DuplicateIcon className="w-8 h-8 sm:w-10 sm:h-10 pr-1" />
              </button>
            )}
          </div>

          {/* Nutrition Stats */}
          <div className="flex items-center justify-between gap-8 mb-4 border-[0.5px] border-[#E5E5EA] rounded-md px-4 py-3">
            {[
              { label: "Cal -",   value: `${Math.round(totals.cal)}`,      color: "#B44AE6", unit: "" },
              { label: "Carb -",  value: `${Math.round(totals.carb)}g`,    color: "#FFB532", unit: "" },
              { label: "Fats -",  value: `${Math.round(totals.fat)}g`,     color: "#F25BEC", unit: "" },
              { label: "A.pro -", value: `${Math.round(totals.protein)}g`, color: "#6BBDFA", unit: "" },
              { label: "GL -",    value: `${Math.round(totals.gl)}`,       color: "#A0CA07", unit: "" },
              { label: "C:P:F",   value: cpfRatio,                         color: "#007AFF", unit: "" },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center gap-2">
                <span className="text-sm text-[#1F1F1F]">{label}</span>
                <span className="text-sm font-medium" style={{ color }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Dishes Table */}
          <div className="mb-4 h-[250px] bg-[#FAFAFA] p-4 rounded-md overflow-y-auto custom-scrollbar-overlay pr-2">
            <div className="grid grid-cols-[3fr_1.8fr_0.8fr_0.6fr_0.6fr_0.6fr_0.6fr_0.6fr] gap-3 mb-3 text-[12px] 3xl:text-[14px] text-[#646464] font-inter font-medium border-b-[0.5px] border-[#E5E5EA] pb-3">
              <div>Name</div><div>Units</div><div>Quantity</div>
              <div className="text-center">Cal</div>
              <div className="text-center">GL</div><div className="text-center">Carb</div>
              <div className="text-center">Protein</div><div className="text-center">Fat</div>
            </div>
            {dishes.filter((d) => d.name).map((dish) =>
              dish.is_hidden ? (
                <div
                  key={dish.id}
                  className="grid grid-cols-[3fr_1.8fr_0.8fr_0.6fr_0.6fr_0.6fr_0.6fr_0.6fr] gap-3 mb-2 items-center"
                >
                  <div className="px-2 py-1.5 border border-[#FFCDD2] bg-[#FFF5F5] rounded-md text-sm text-[#1F1F1F] truncate">
                    {dish.name}
                  </div>
                  <div className="text-sm text-[#FF6B35] font-medium">Hidden won't be added</div>
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="text-sm text-center text-[#C7C7CC]">-</div>
                  ))}
                </div>
              ) : (
                <div
                  key={dish.id}
                  className="grid grid-cols-[3fr_1.8fr_0.8fr_0.6fr_0.6fr_0.6fr_0.6fr_0.6fr] gap-3 mb-2 items-center"
                >
                  <div
                    className="text-sm text-[#1F1F1F] truncate cursor-default"
                    title={dish.name}
                  >
                    {dish.name}
                  </div>
                  <div
                    className="text-sm text-[#1F1F1F] truncate cursor-default"
                    title={formatUnitLabel(dish.name, dish.unit)}
                  >
                    {formatUnitLabel(dish.name, dish.unit)}
                  </div>
                  <div className="text-sm text-[#1F1F1F]">{dish.quantity}</div>

                  {[dish.cal, dish.gl, dish.carb, dish.protein, dish.fat].map((v, i) => (
                    <div key={i} className="text-sm text-center text-[#1F1F1F]">
                      {v.toFixed(0).padStart(2, "0")}
                    </div>
                  ))}
                </div>
              )
            )}
          </div>

          {/* Instructions */}
          {instructions && instructions.trim() && (
            <div className="bg-[#F9FEFE] border-[0.5px] border-[#E5E5EA] rounded-md p-4">
              <p className="text-sm text-[#1F1F1F] leading-relaxed">{instructions}</p>
            </div>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────────────────── */}
        <div className="border-t border-[#E5E5EA] p-4 flex justify-between items-center">

          {/* Left — delete (hidden in view-only mode) */}
          <div className="relative h-10 flex items-center min-w-0">
            {!isViewOnly && onDelete && (
              <>
                {showDeleteConfirmation ? (
                  <div 
                    className="flex items-center gap-3"
                    style={{
                      animation: 'slideInFromDeleteIcon 0.2s ease-out forwards'
                    }}
                  >
                    <Button
                      variant="danger"
                      onClick={onDelete}
                      disabled={isDeleting}
                      className="whitespace-nowrap"
                    >
                      {isDeleting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Deleting...
                        </>
                      ) : (
                        "Confirm delete"
                      )}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setShowDeleteConfirmation(false)}
                      disabled={isDeleting}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowDeleteConfirmation(true)}
                    disabled={isDeleting}
                    className="text-[#FF3B30] hover:text-[#E63228] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DeleteIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                )}
              </>
            )}
          </div>

          {/* Right — Edit button (view-only → Close instead) */}
          {isViewOnly || !onEdit ? (
            <Button
              variant="secondary"
              size="md"
              onClick={onClose}
            >
              Close
            </Button>
          ) : (
            <button
              onClick={onEdit}
              disabled={isDeleting}
              className="px-6 py-2 bg-white text-[#1F1F1F] hover:bg-[#0DB5B5] hover:text-white hover:border-none rounded-md text-sm font-medium border-[0.5px] border-[#1F1F1F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Inline CSS for slide animation */}
      <style>{`
        @keyframes slideInFromDeleteIcon {
          from {
            transform: translateX(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ViewMealOptionModal;